import { constantize } from "shared/helpers/string";
import type { SourceGroup } from "shared/types/mentions";

import type { MediumField } from "./media";
import { getSources } from "./media";

export type Filter = {
  action: SourceAction;
  id?: string | number;
  name?: string;
  scope: string;
  target_id: string | number;
  target_type: string;
};

interface SourceTopic {
  id: number;
  name: string;
}

export interface Source {
  action?: SourceAction;
  category?: string;
  filters?: Filter[];
  id: number;
  location: string;
  logo_url?: string;
  medium: MediumField;
  name: string;
  sector?: string;
  source_group?: SourceGroup;
  source_group_and_associated_ids?: string[] | number[];
  source_group_name?: string;
  target_type?: string;
  topics?: SourceTopic[];
  type: string;
}
export interface SourceParams {
  action: SourceAction;
  id: number;
  target_id: number;
  target_type: string;
}

export type SourceAction = "include" | "exclude";

export type SourceTab = "include" | "exclude";

function isSourceGroupMediumLocation(item: Source): boolean {
  return (
    item.type === "source_group_medium_location" ||
    item.target_type === "SourceGroupMediumLocation"
  );
}

function includedSources(streamSources: Source[]): Source[] {
  return streamSources.map((source) => ({
    ...source,
    action: "include",
    target_id: source.id,
    target_type: source.target_type || constantize(source.type),
  }));
}

function excludedSources(streamSources: Source[]): Source[] {
  return streamSources.map((source) => ({
    ...source,
    action: "exclude",
    target_id: source.id,
    target_type: source.target_type || constantize(source.type),
  }));
}

function sourcesToParams(
  streamSources: Source[],
  action: SourceAction
): SourceParams[] {
  return streamSources.map((source) => ({
    action,
    id: source.id,
    target_id: source.id,
    target_type: source.target_type || constantize(source.type),
  }));
}

function sourceGroupFilters(
  originalFilters: Filter[],
  includedSourcesParams: Source[],
  excludedSourcesParams: Source[]
): Filter[] {
  const processSourceGroups = (
    streamSources: Source[],
    action: SourceAction
  ): Filter[] =>
    streamSources.flatMap((source) => {
      if (!isSourceGroupMediumLocation(source)) {
        return [];
      }

      return (source.source_group_and_associated_ids || []).flatMap((id) =>
        getSources().map(({ field }) => ({
          action,
          scope: field,
          target_id: id,
          target_type: "SourceGroup",
        }))
      );
    });

  const filters: Filter[] = (originalFilters || []).filter(
    (filter) => filter.target_type !== "SourceGroup"
  );

  filters.push(
    ...processSourceGroups(includedSourcesParams, "include"),
    ...processSourceGroups(excludedSourcesParams, "exclude")
  );

  return filters;
}

function sourceKeysFor(sources: Source[]): string[] {
  return sources.flatMap((source) => {
    if (!isSourceGroupMediumLocation(source)) {
      return `${source.medium.toLowerCase()}_${source.id}`;
    }

    return [];
  });
}

function sourceGroupKeys(sources: Source[]): string[] {
  return sources.flatMap((source) => {
    if (!isSourceGroupMediumLocation(source)) return [];

    return (
      source.source_group_and_associated_ids?.flatMap((id) =>
        getSources().map(({ field }) => `${field}_${id}`)
      ) || []
    );
  });
}

export {
  excludedSources,
  includedSources,
  isSourceGroupMediumLocation,
  sourceGroupFilters,
  sourceGroupKeys,
  sourceKeysFor,
  sourcesToParams,
};
